import { Loader2 } from "lucide-react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "../../../../components/ui/common/alert-dialog";
import { isDemoRecipe, toRecipeRecord } from "../recipeList.utils";
import { RecipeGroupRecord } from "../types";

interface ConfirmDialogConfig {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void;
  destructive?: boolean;
  isConfirmLoading?: boolean;
}

const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  destructive = false,
  isConfirmLoading = false
}: ConfirmDialogConfig) => (
  <AlertDialog
    open={open}
    onOpenChange={openState => {
      if (!isConfirmLoading) {
        onOpenChange(openState);
      }
    }}
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isConfirmLoading}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={event => {
            event.preventDefault();
            onConfirm();
          }}
          disabled={isConfirmLoading}
          className={
            destructive
              ? "tw-bg-destructive tw-text-destructive-foreground hover:tw-bg-destructive/90"
              : undefined
          }
        >
          {isConfirmLoading ? (
            <>
              <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
              {confirmLabel}
            </>
          ) : (
            confirmLabel
          )}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

interface RecipeListConfirmDialogsProps {
  demoConfirmOpen: boolean;
  onDemoConfirmOpenChange: (open: boolean) => void;
  onConfirmMakeDemo: () => void;
  deleteConfirmOpen: boolean;
  deleteConfirmTarget: RecipeGroupRecord | null;
  onDeleteConfirmOpenChange: (open: boolean) => void;
  onConfirmDelete: () => void;
  publicConfirmOpen: boolean;
  onPublicConfirmOpenChange: (open: boolean) => void;
  onConfirmMakePublic: () => void;
  privateConfirmOpen: boolean;
  onPrivateConfirmOpenChange: (open: boolean) => void;
  onConfirmMakePrivate: () => void;
  privateConfirmTarget: RecipeGroupRecord | null;
  isDeleteConfirmLoading?: boolean;
  isDemoConfirmLoading?: boolean;
  isPublicConfirmLoading?: boolean;
  isPrivateConfirmLoading?: boolean;
}

const getDeleteDialogContent = (recipe: RecipeGroupRecord | null) => {
  if (recipe && isDemoRecipe(toRecipeRecord(recipe))) {
    return {
      title: "Delete Demo Kit",
      description:
        "You are about to delete a demo kit. Users currently using it will be notified, and it will no longer be available."
    };
  }

  return {
    title: "Delete Recipe Simulator",
    description: recipe
      ? `Are you sure you want to remove "${recipe.recipeName}"?`
      : "Are you sure you want to remove this recipe simulator?"
  };
};

const RecipeListConfirmDialogs = ({
  demoConfirmOpen,
  onDemoConfirmOpenChange,
  onConfirmMakeDemo,
  deleteConfirmOpen,
  deleteConfirmTarget,
  onDeleteConfirmOpenChange,
  onConfirmDelete,
  publicConfirmOpen,
  onPublicConfirmOpenChange,
  onConfirmMakePublic,
  privateConfirmOpen,
  onPrivateConfirmOpenChange,
  onConfirmMakePrivate,
  privateConfirmTarget,
  isDeleteConfirmLoading = false,
  isDemoConfirmLoading = false,
  isPublicConfirmLoading = false,
  isPrivateConfirmLoading = false
}: RecipeListConfirmDialogsProps) => {
  const deleteDialogContent = getDeleteDialogContent(deleteConfirmTarget);

  return (
  <>
    <ConfirmDialog
      open={demoConfirmOpen}
      onOpenChange={onDemoConfirmOpenChange}
      title="Make Demo Kit"
      description="Are you sure you want to mark this recipe as a demo kit? It will be visible to all users in the system."
      confirmLabel="Confirm"
      onConfirm={onConfirmMakeDemo}
      isConfirmLoading={isDemoConfirmLoading}
    />
    <ConfirmDialog
      open={deleteConfirmOpen}
      onOpenChange={onDeleteConfirmOpenChange}
      title={deleteDialogContent.title}
      description={deleteDialogContent.description}
      confirmLabel="Delete"
      onConfirm={onConfirmDelete}
      destructive
      isConfirmLoading={isDeleteConfirmLoading}
    />
    <ConfirmDialog
      open={publicConfirmOpen}
      onOpenChange={onPublicConfirmOpenChange}
      title="Make Recipe Public"
      description="Are you sure you want to make this recipe public? Anyone will be able to view and download the report, but only you can update it."
      confirmLabel="Confirm"
      onConfirm={onConfirmMakePublic}
      isConfirmLoading={isPublicConfirmLoading}
    />
    <ConfirmDialog
      open={privateConfirmOpen}
      onOpenChange={onPrivateConfirmOpenChange}
      title="Make Recipe Private"
      description={
        privateConfirmTarget
          ? `Are you sure you want to make "${privateConfirmTarget.recipeName}" private? It will no longer be visible to other users.`
          : "Are you sure you want to make this recipe private? It will no longer be visible to other users."
      }
      confirmLabel="Confirm"
      onConfirm={onConfirmMakePrivate}
      isConfirmLoading={isPrivateConfirmLoading}
    />
  </>
  );
};

export default RecipeListConfirmDialogs;
